/**
 * PATH: packages/ui/src/kanban/use-triage-tab-url.ts
 * FILE_ID: QUI-KANBAN-TRIAGE-URL-001
 * PURPOSE: Sync the Triage lane's UC|AI tab state to the page URL
 *          so the operator's filter survives a page refresh, link
 *          share, or browser back/forward navigation.
 * WHY: Module D EMAIL_KANBAN_ALLOCATION_PLAN.md follow-up. Operators
 *      share board links in chat ("look at the AI suggestions
 *      bucket in Inbox-Private"); without URL persistence the
 *      recipient always lands on the default `all` tab.
 *
 * FLOW:
 *   ┌─────────────┐    ┌────────────────────┐    ┌────────────────────┐
 *   │ Page URL    │ →  │ Parse ?triage=… on │ →  │ Tab state          │
 *   │ ?triage=ai  │    │ initial render +   │    │ ('all'|'uc'|'ai')  │
 *   └─────────────┘    │ on popstate        │    └────────────────────┘
 *                      └────────────────────┘             │
 *                                ↑                        ▼
 *                       ┌────────┴──────────┐    ┌────────────────────┐
 *                       │ window.history…   │ ←  │ User clicks UC tab │
 *                       │ replaceState      │    └────────────────────┘
 *                       └───────────────────┘
 *
 * Pure FE — no router dependency, no SSR coupling. Works under
 * react-router because we use history.replaceState (which RR
 * picks up via its own popstate listener on the next navigation).
 *
 * SSR / window-undefined safety: every browser access is guarded.
 *
 * DEPENDENCIES:
 *   - react: useState, useEffect, useCallback
 *
 * RELATED:
 *   - components/KanbanLane.tsx — call site (replaces local useState)
 */

import { useCallback, useEffect, useState } from 'react'

export type TriageTab = 'all' | 'uc' | 'ai'

/**
 * Lane-scoped URL key. Multiple Triage lanes can be visible at once
 * (e.g. board has both Triage + Inbox-Private columns), so we encode
 * the lane id into the key to keep them independent.
 *
 * Example URL: `?triage_lane_123=ai&triage_lane_456=uc`
 *
 * If `urlScope` is `'global'` (default) we keep a single `?triage`
 * key — that matches Trello's UX where the user only ever interacts
 * with one Triage column at a time. The lane-scoped form is exposed
 * for boards that intentionally show two Triage lanes side by side.
 */
export interface UseTriageTabUrlOptions {
  /** Default tab when the URL has no value. */
  initial?: TriageTab
  /** When set, the URL key becomes `triage_<scope>` so multiple lanes
   *  can persist independent state on the same URL. Defaults to
   *  shared `?triage` — every Triage lane on the page reads/writes
   *  the same key, which is what 99% of boards want. */
  urlScope?: string | null
  /** Disables writes to the URL (e.g. tests, storybook). Reads still
   *  happen on mount so deep links keep working. */
  readOnly?: boolean
}

const VALID_TABS = new Set<TriageTab>(['all', 'uc', 'ai'])

function urlKey(scope: string | null | undefined): string {
  return scope ? `triage_${scope}` : 'triage'
}

function readFromUrl(scope: string | null | undefined, fallback: TriageTab): TriageTab {
  if (typeof window === 'undefined') return fallback
  try {
    const params = new URLSearchParams(window.location.search)
    const raw = params.get(urlKey(scope))
    return raw && VALID_TABS.has(raw as TriageTab) ? (raw as TriageTab) : fallback
  } catch {
    // URL parsing failures (very rare — malformed query) shouldn't
    // brick the UI. Fall back silently.
    return fallback
  }
}

function writeToUrl(scope: string | null | undefined, value: TriageTab, initial: TriageTab) {
  if (typeof window === 'undefined') return
  try {
    const url = new URL(window.location.href)
    const key = urlKey(scope)
    if (value === initial) {
      // Default value → keep URL clean by removing the key entirely.
      url.searchParams.delete(key)
    } else {
      url.searchParams.set(key, value)
    }
    const next = `${url.pathname}${url.search}${url.hash}`
    window.history.replaceState(window.history.state, '', next)
  } catch {
    // history failures shouldn't propagate; local state stays correct.
  }
}

/**
 * `useTriageTabUrl` — the Triage UC|AI tab state, bidirectionally
 * synced with `?triage=` (or `?triage_<scope>=`) in the URL.
 *
 * Returns `[tab, setTab]` in the same shape as `useState`, so it's
 * a drop-in replacement for the previous local-state version.
 *
 * Reacts to browser back/forward via the `popstate` event so the
 * tab updates when the user navigates history.
 */
export function useTriageTabUrl({
  initial = 'all',
  urlScope = null,
  readOnly = false,
}: UseTriageTabUrlOptions = {}): [TriageTab, (next: TriageTab) => void] {
  const [tab, setTabState] = useState<TriageTab>(() => readFromUrl(urlScope, initial))

  // Subscribe to popstate so back/forward syncs the tab.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const onPop = () => setTabState(readFromUrl(urlScope, initial))
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [initial, urlScope])

  const setTab = useCallback(
    (next: TriageTab) => {
      setTabState(next)
      if (!readOnly) writeToUrl(urlScope, next, initial)
    },
    [initial, readOnly, urlScope],
  )

  return [tab, setTab]
}
